import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Wrench,
  ShoppingCart,
  Calculator,
  UserCheck,
  BookOpen,
  Car,
  Users,
  User,
  FileText,
  Target,
  Mail,
  LogOut,
  X,
  ClipboardList,
  PartyPopper,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useResponsive } from '@/hooks/useResponsive';
import { getProfilePictureUrl } from '@/utils/imageUtils';
import { hasPermission } from '@/utils/permissions';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  onMobileClose: () => void;
}

const navigationItems = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, permission: 'dashboard.view' },
  { name: 'Anniversary Hustle', href: '/anniversary-hustle', icon: PartyPopper },
  { name: 'Technical', href: '/technical', icon: Wrench, permission: 'technical.view' },
  { name: 'Sales', href: '/sales', icon: ShoppingCart, permission: 'sales.view' },
  { name: 'Accounts', href: '/accounts', icon: Calculator, permission: 'accounts.view' },
  { name: 'Requisitions', href: '/requisitions', icon: ClipboardList, permission: 'requisitions.view' },
  { name: 'HR', href: '/hr', icon: UserCheck, permission: 'hr.view' },
  { name: 'Library', href: '/library', icon: BookOpen, permission: 'library.view' },
  { name: 'Vehicles', href: '/vehicles', icon: Car, permission: 'vehicles.view' },
  { name: 'Users', href: '/users', icon: Users, permission: 'users.view' },
  { name: 'Profile', href: '/profile', icon: User, permission: 'profile.view' },
  { name: 'Reports', href: '/reports', icon: FileText, permission: 'reports.view' },
  { name: 'Target', href: '/target', icon: Target, permission: 'target.manage' },
  { name: 'Newsletter', href: '/newsletter', icon: Mail, permission: 'newsletter.view' },
];

export function Sidebar({ isCollapsed, onToggle, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { isMobile } = useResponsive();
  const navRef = useRef<HTMLElement>(null);

  const handleLogout = () => {
    logout();
  };

  // Function to check if user has access to a navigation item
  const hasAccess = (permission?: string) => hasPermission(user, permission);

  // Filter navigation items based on user role
  const filteredNavigationItems = navigationItems.filter(item => hasAccess(item.permission));

  // Auto-scroll to active item when location changes
  useEffect(() => {
    if (navRef.current) {
      const activeItem = navRef.current.querySelector('[data-active="true"]') as HTMLElement;
      if (activeItem) {
        // Scroll the active item into view with smooth behavior
        activeItem.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }
  }, [location.pathname]);

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && !isCollapsed && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full bg-gradient-to-b from-white via-gray-50/50 to-white z-50 transition-all duration-300 shadow-2xl border-r border-gray-100 flex flex-col
        ${isMobile ? (isCollapsed ? '-translate-x-full' : 'translate-x-0 w-80') : 'w-80 translate-x-0'}
        lg:translate-x-0 backdrop-blur-sm
      `}>
        {/* Header */}
        <div className="relative p-6 border-b border-gray-100/50 flex-shrink-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-indigo-50/20 to-purple-50/30"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_70%)]"></div>
          
          <div className="relative flex justify-center">
            <div className="relative group">
              <div className="absolute inset-0 rounded-3xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300 animate-pulse" style={{ backgroundColor: '#001F54' }}></div>
              <div className="relative w-20 h-20 rounded-3xl flex items-center justify-center shadow-2xl ring-4 ring-offset-4 ring-offset-white animate-pulse transform hover:scale-105 transition-transform duration-300" style={{ backgroundColor: '#001F54', '--tw-ring-color': 'rgba(0, 31, 84, 0.2)' } as React.CSSProperties}>
                <img 
                  src="/login_logo.png" 
                  alt="Aura Logo" 
                  className="w-12 h-12 object-contain filter brightness-0 invert drop-shadow-lg"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
                <span className="text-white font-bold text-3xl hidden drop-shadow-lg">A</span>
              </div>
            </div>
          </div>

          {/* Mobile Close Button */}
          {isMobile && (
            <button
              onClick={onMobileClose}
              className="absolute top-6 right-6 p-3 rounded-2xl hover:bg-white/80 backdrop-blur-sm transition-all duration-200 lg:hidden shadow-lg border border-gray-200/50"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav ref={navRef} className="flex-1 px-8 py-3 space-y-3 overflow-y-auto sidebar-scroll">
          <div className="mb-4">
            <div className="flex items-center space-x-2 px-4">
              <div className="w-8 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"></div>
              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">Navigation</p>
              <div className="flex-1 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"></div>
            </div>
          </div>
          
          {filteredNavigationItems.map((item, index) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;
            
            return (
              <div key={item.name} className="relative" data-active={isActive}>
                <Link
                  to={item.href}
                  onClick={isMobile ? onMobileClose : undefined}
                  className={`
                    group relative flex items-center px-6 py-4 rounded-2xl text-sm font-semibold transition-all duration-500 overflow-hidden
                    ${isActive 
                      ? 'bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white shadow-2xl shadow-blue-500/30 transform scale-[1.03] border border-blue-400/30' 
                      : 'text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50/50 hover:text-gray-900 hover:shadow-lg hover:transform hover:scale-[1.02] border border-transparent hover:border-blue-200/50'
                    }
                  `}
                  style={{
                    animationDelay: `${index * 100}ms`
                  }}
                >
                  {/* Animated background for active item */}
                  {isActive && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 via-indigo-400/20 to-purple-400/20 rounded-2xl blur-xl animate-pulse"></div>
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-indigo-600/10 rounded-2xl"></div>
                    </>
                  )}
                  
                  {/* Hover glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-indigo-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  <div className={`relative flex items-center w-full ${isMobile && isCollapsed ? 'justify-center' : ''}`}>
                    <div className={`
                      flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300
                      ${isActive 
                        ? 'bg-white/20 backdrop-blur-sm shadow-lg' 
                        : 'bg-gray-100 group-hover:bg-blue-100 group-hover:shadow-md'
                      }
                    `}>
                      <Icon className={`w-5 h-5 transition-all duration-300 ${isActive ? 'text-white drop-shadow-sm' : 'text-gray-600 group-hover:text-blue-600'}`} />
                    </div>
                    
                    {(!isMobile || !isCollapsed) && (
                      <span className="ml-4 font-bold tracking-wide">{item.name}</span>
                    )}
                  </div>
                  
                  {/* Active indicator */}
                  {isActive && (
                    <div className="absolute right-4 flex items-center space-x-1">
                      <div className="w-2 h-2 bg-white rounded-full shadow-lg animate-pulse"></div>
                      <div className="w-1 h-1 bg-white/70 rounded-full"></div>
                    </div>
                  )}
                  
                  {/* Animated border effect */}
                  <div className={`absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-blue-400 via-indigo-500 to-purple-500 rounded-r-full transition-all duration-500 ${isActive ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 group-hover:opacity-100 group-hover:scale-y-100'}`}></div>
                  
                  {/* Ripple effect on hover */}
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse"></div>
                </Link>
              </div>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="relative border-t border-gray-100/50 p-4 flex-shrink-0">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50/30 via-blue-50/20 to-indigo-50/30"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_100%,rgba(59,130,246,0.05),transparent_70%)]"></div>
          
          <div className="relative flex items-center space-x-3 mb-4">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-teal-600 rounded-xl blur-sm opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
              <div className="relative w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg ring-2 ring-emerald-400/20 transform hover:scale-105 transition-transform duration-300 overflow-hidden">
                {user?.profile_picture_url ? (
                  <img 
                    src={getProfilePictureUrl(user.profile_picture_url) || ''} 
                    alt="Profile" 
                    className="w-full h-full object-cover rounded-xl"
                    onError={(e) => {
                      // Fallback to initials if image fails to load
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <span className={`text-white font-bold text-sm drop-shadow-sm ${user?.profile_picture_url ? 'hidden' : ''}`}>
                  {user?.nickname?.charAt(0) || user?.name?.charAt(0) || user?.full_name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm flex items-center justify-center">
                <div className="w-1 h-1 bg-white rounded-full animate-pulse"></div>
              </div>
            </div>
            
            {(!isMobile || !isCollapsed) && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate">
                  {user?.nickname || user?.name || user?.full_name || 'User'}
                </p>
                <p className="text-xs text-gray-600 truncate">
                  {user?.email || 'user@example.com'}
                </p>
              </div>
            )}
          </div>
          
          <button
            onClick={handleLogout}
            className={`
              relative w-full flex items-center px-4 py-2.5 text-sm font-semibold
              bg-gradient-to-r from-red-50 via-red-100 to-red-50 text-red-700
              hover:from-red-100 hover:via-red-200 hover:to-red-100 hover:text-red-800
              rounded-xl transition-all duration-300 group overflow-hidden
              border border-red-200/50 hover:border-red-300 shadow-sm hover:shadow-md
              transform hover:scale-[1.01]
              ${isMobile && isCollapsed ? 'justify-center' : ''}
            `}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-red-100/30 via-red-200/30 to-red-100/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
            
            <div className="relative flex items-center">
              <div className="flex items-center justify-center w-6 h-6 bg-red-200/50 rounded-lg mr-2 group-hover:bg-red-300/50 transition-colors duration-300">
                <LogOut className="w-4 h-4 transition-all duration-300 group-hover:scale-110" />
              </div>
              {(!isMobile || !isCollapsed) && (
                <span className="relative font-semibold tracking-wide">Logout</span>
              )}
            </div>
            
            {/* Animated border */}
            <div className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-red-400 to-red-600 transition-all duration-300 group-hover:w-full w-0"></div>
          </button>
        </div>
      </div>
    </>
  );
}
